// src/components/AnnouncementModal.js
//
// One-time announcement popup shown on the Dashboard to every logged-in user. It lists the latest
// features and explains how attendance, absence and work hours are calculated. Dismissal is stored
// in localStorage keyed by a version string — bump ANNOUNCEMENT_VERSION to re-show a new notice.

import React, { useState } from 'react';
import { FiX, FiInfo } from 'react-icons/fi';

const ANNOUNCEMENT_VERSION = 'v1-2026-07';
const STORAGE_KEY = `mam_announcement_seen_${ANNOUNCEMENT_VERSION}`;

export default function AnnouncementModal() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== 'true';
    } catch {
      return true;
    }
  });

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* ignore (private mode) */
    }
    setOpen(false);
  };

  if (!open) return null;

  const section = { marginBottom: 18 };
  const sectionTitle = {
    display: 'flex', alignItems: 'center', gap: 8,
    fontWeight: 900, fontSize: 15, color: '#111827', margin: '0 0 8px',
  };
  const li = { margin: '0 0 7px', lineHeight: 1.8, color: '#374151', fontSize: 13.5 };
  const strong = { color: '#111827', fontWeight: 800 };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={close}
    >
      <div
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 18, width: '100%', maxWidth: 560,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
          fontFamily: 'Nunito, Segoe UI, sans-serif', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
            padding: '18px 22px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.22)',
                display: 'grid', placeItems: 'center', color: '#fff', fontSize: 20, flexShrink: 0,
              }}
            >
              📢
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 900, fontSize: 17 }}>تحديثات جديدة على النظام</div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12.5, marginTop: 2 }}>
                الإضافات الجديدة وطريقة احتساب الحضور والغياب وساعات العمل
              </div>
            </div>
          </div>
          <button
            onClick={close}
            aria-label="إغلاق"
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              width: 34, height: 34, borderRadius: 10, cursor: 'pointer',
              display: 'grid', placeItems: 'center', fontSize: 17, flexShrink: 0,
            }}
          >
            <FiX />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', overflowY: 'auto' }}>
          {/* New features */}
          <div style={section}>
            <div style={sectionTitle}>✨ الإضافات الجديدة</div>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              <li style={li}>
                <span style={strong}>احتساب الغياب عند نسيان تسجيل الخروج:</span> إذا سجّلت دخولك ونسيت تسجيل الخروج،
                يُحتسب اليوم <span style={strong}>غيابًا</span> وتُلغى ساعاته. يظهر لك تنبيه لمدة ٢٤ ساعة في الصفحة
                الرئيسية، وتقدر تقدّم طلبًا للمدير بوقت مغادرتك ليُعاد احتسابه دوامًا.
              </li>
              <li style={li}>
                <span style={strong}>طلب ساعات إضافية:</span> من صفحة «ملفي الشخصي» ترسل للمدير ساعات عملتها خارج
                الدوام (مثلاً عوّضت تأخيرك بالعمل من البيت) مع وصف ما عملت عليه — وتُضاف لساعاتك بعد الموافقة.
              </li>
              <li style={li}>
                <span style={strong}>جدول دوام لكل موظف:</span> المدير يحدّد أيام الدوام وعدد الساعات المطلوبة يوميًا
                لكل موظف على حدة.
              </li>
              <li style={li}>
                <span style={strong}>ملخّص شهري في ملفك الشخصي:</span> تشوف ساعاتك المُنجزة، الساعات المطلوبة، المتبقّي،
                الغيابات، والإجازات المدفوعة/غير المدفوعة.
              </li>
              <li style={li}>
                <span style={strong}>طلبات وإشعارات:</span> طلباتك تصل المدير مباشرة، ويصله إشعار عند كل طلب، وتظهر في
                صفحة «ساعات العمل».
              </li>
              <li style={li}>
                <span style={strong}>تصميم جديد ومحسّن</span> لعدّة صفحات (الموظفين، المتدربين، العملاء، الحملات،
                التقارير، الإشعارات، وغيرها).
              </li>
            </ul>
          </div>

          {/* Work hours */}
          <div style={section}>
            <div style={sectionTitle}>🕒 طريقة احتساب الحضور وساعات العمل</div>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              <li style={li}>
                سجّل <span style={strong}>دخولك (Check In)</span> عند بدء الدوام و<span style={strong}>خروجك (Check Out)</span>
                {' '}عند المغادرة، من بطاقة الحضور في الصفحة الرئيسية.
              </li>
              <li style={li}>
                <span style={strong}>ساعات العمل</span> = وقت الخروج − وقت الدخول (+ أي ساعات إضافية معتمدة من المدير).
              </li>
              <li style={li}>
                <span style={strong}>الساعات المطلوبة شهريًا</span> = أيام دوامك × الساعات المطلوبة يوميًا (يحدّدها المدير).
              </li>
              <li style={li}>
                <span style={strong}>المتبقّي</span> = الساعات المطلوبة − الساعات المُنجزة.
              </li>
            </ul>
          </div>

          {/* Absence */}
          <div style={section}>
            <div style={sectionTitle}>🚫 طريقة احتساب الغياب</div>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              <li style={li}>إذا لم تسجّل دخولًا في يوم دوام → <span style={strong}>غياب</span>.</li>
              <li style={li}>
                إذا سجّلت دخولًا ولم تسجّل خروجًا → <span style={strong}>غياب</span> (وتُلغى ساعات اليوم) — إلا إذا
                وافق المدير على طلبك بوقت المغادرة.
              </li>
              <li style={li}>
                <span style={strong}>عدد الغيابات</span> = أيام الدوام − أيام الحضور − أيام الإجازة (حسب جدولك الخاص).
              </li>
              <li style={li}>الأيام غير المجدولة (مثل الجمعة) لا تُحتسب دوامًا ولا غيابًا.</li>
            </ul>
          </div>

          {/* Important note */}
          <div
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12,
              padding: '12px 14px', color: '#92400e', fontSize: 13, lineHeight: 1.8, fontWeight: 700,
            }}
          >
            <FiInfo style={{ flexShrink: 0, marginTop: 3, fontSize: 16 }} />
            <span>
              مهم جدًا: تأكّد دائمًا من <span style={{ fontWeight: 900 }}>تسجيل الخروج (Check Out)</span> قبل مغادرة
              الدوام، حتى لا يُحتسب يومك غيابًا وتُلغى ساعاته.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f3f5', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={close}
            style={{
              background: 'linear-gradient(135deg, #4a90e2, #357abd)', color: '#fff', border: 'none',
              borderRadius: 10, padding: '10px 26px', fontWeight: 800, fontSize: 14, cursor: 'pointer',
              fontFamily: 'inherit', boxShadow: '0 6px 16px rgba(74,144,226,0.3)',
            }}
          >
            فهمت 👍
          </button>
        </div>
      </div>
    </div>
  );
}
